"use client"

import { AlertTriangle, RotateCcw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { LifecycleAlerts } from "@/types/crew"

/**
 * OKS-27 — Lifecycle alert pill (rotation + inactivity).
 *
 * **Wrapper-with-className pattern** (UX Spec V2 lock + Crew hard rule):
 *   this component composes the `<Badge>` primitive without modifying it,
 *   adding a per-variant Tailwind className map + lucide icon + `<Tooltip>`.
 *   Same DOM, same tokens. A wrapper is the right shape here regardless of
 *   whether the primitive could be edited (`npx shadcn add` is banned — see
 *   `docs/frontend/README.md` rule 6).
 *
 * **Visibility is purely BE-driven.** When the `alert` prop is `null`
 * the component renders nothing (no empty-pill, no `—`). When non-null
 * the badge fires; the FE NEVER recomputes thresholds, NEVER reads brand
 * settings, NEVER receives threshold values. Counts come from BE as
 * floor-truncated brand-TZ integers.
 *
 * **Tooltip copy is variant + kind keyed:**
 *   - `variant="rotation"` → single tooltip string keyed on `months`
 *   - `variant="inactivity"` w/ `kind: "post_mission"` → "last mission N weeks ago"
 *   - `variant="inactivity"` w/ `kind: "joined_fallback"` → "joined N weeks ago, no mission completed yet"
 *
 * **A11y contract:**
 *   - Each pill is a `<button type="button">` with `aria-label` =
 *     full tooltip text — screen readers announce the same content
 *     sighted users see in the tooltip; color is never sole indicator
 *     (icon + text label always render).
 *   - Tap target ≥44×44 via invisible `before:absolute` padding (the
 *     intrinsic Badge is ~20px tall; the focusable wrapper expands the
 *     hit area without changing visual layout).
 *   - Radix `<Tooltip>` wires `aria-describedby` automatically and
 *     respects `prefers-reduced-motion`; ESC closes + restores focus.
 *
 * **`data-variant` + `data-kind` attrs** mirror the BE wire field names
 * verbatim so QA + UX can scope without leaking through testids.
 */

type Variant = "rotation" | "inactivity"

type RotationAlert = NonNullable<LifecycleAlerts["rotation"]>
type InactivityAlert = NonNullable<LifecycleAlerts["inactivity"]>

interface VisualSpec {
  /** Per-variant Tailwind class string applied to the inner Badge. */
  className: string
  Icon: typeof AlertTriangle
}

/**
 * UX Spec V2 §Badge component spec — light/dark color tokens verified
 * ≥4.5:1 AA on both surfaces (5.81:1 inactivity, 6.42:1 rotation).
 */
const VISUAL_SPEC: Record<Variant, VisualSpec> = {
  inactivity: {
    // Dark text token `red-300` (#FCA5A5) per UX Spec V2 lock — chosen
    // for AA contrast on the deepened dark bg; ratchet from `red-200`
    // was in the safer direction but not signed off.
    className:
      "border-red-200 bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/60",
    Icon: AlertTriangle,
  },
  rotation: {
    // Dark text token `amber-300` (#FCD34D) per UX Spec V2 lock — same
    // rationale as inactivity above.
    className:
      "border-amber-200 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/60",
    Icon: RotateCcw,
  },
}

export interface LifecycleAlertBadgeProps {
  variant: Variant
  /**
   * The corresponding `lifecycle_alerts.{rotation|inactivity}` slot.
   * Object-presence drives render: `null` → component returns `null`.
   */
  alert: RotationAlert | InactivityAlert | null
  /**
   * Surface-prefixed testid (consumer-supplied per testid contract):
   *   - roster row: `crew-list-row-alert-{rotation|inactivity}`
   *   - detail header: `creator-detail-alert-{rotation|inactivity}`
   */
  testId: string
  /**
   * Optional surface-prefixed tooltip testid for QA scoping. When
   * omitted, the tooltip content has no testid (rare — both surfaces
   * pass one).
   */
  tooltipTestId?: string
  className?: string
}

export function LifecycleAlertBadge({
  variant,
  alert,
  testId,
  tooltipTestId,
  className,
}: LifecycleAlertBadgeProps) {
  const { t } = useTranslation()

  if (!alert) return null

  const spec = VISUAL_SPEC[variant]
  const { Icon } = spec

  // Resolve label / aria / tooltip via literal `t(...)` arguments so the
  // inertia_i18n health-scanner can statically prove every key is reachable.
  // Dynamic template strings (`t(\`crew.lifecycle.${variant}.tooltip\`)`)
  // bypass the scanner — see crew-member-status-badge.tsx for the same
  // pattern. i18next CLDR `_one`/`_other` plural variants are resolved
  // automatically from `{ count: N }`.
  let label: string
  let ariaText: string
  let tooltipText: string
  let dataKind: InactivityAlert["kind"] | undefined

  if (variant === "rotation" && "months" in alert) {
    const count = alert.months
    label = t("crew.lifecycle.rotation.label")
    tooltipText = t("crew.lifecycle.rotation.tooltip", { count })
    // Aria text mirrors the visible tooltip 1:1 — sighted + screen-reader
    // users hear the same content. UX V2 §Accessibility.
    ariaText = tooltipText
  } else if (variant === "inactivity" && "kind" in alert) {
    const count = alert.weeks
    label = t("crew.lifecycle.inactivity.label")
    dataKind = alert.kind
    if (alert.kind === "post_mission") {
      tooltipText = t("crew.lifecycle.inactivity.tooltip_post_mission", {
        count,
      })
    } else {
      tooltipText = t("crew.lifecycle.inactivity.tooltip_joined_fallback", {
        count,
      })
    }
    ariaText = tooltipText
  } else {
    // Defensive: variant + alert shape mismatch (TS shouldn't allow it).
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-testid={testId}
            data-variant={variant}
            {...(dataKind ? { "data-kind": dataKind } : {})}
            aria-label={ariaText}
            // Tooltip-only affordance — pill clicks must not bubble to a
            // parent <a> (mobile card) or row-onClick handler. Radix
            // Tooltip handles the open/close; we just stop propagation.
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
            }}
            className={cn(
              // Hit-area expander: invisible 44×44 ::before keeps the
              // visible badge tiny while satisfying WCAG 2.5.5 tap target.
              "relative inline-flex items-center rounded-full",
              "before:absolute before:top-1/2 before:left-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
              "focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none",
              className,
            )}
          >
            <Badge
              variant="outline"
              // Inner Badge is the visible chrome; gap-1 keeps icon ↔ label
              // tight per UX Spec V2 §Badge component spec.
              className={cn("gap-1", spec.className)}
            >
              <Icon aria-hidden="true" className="size-3" />
              <span>{label}</span>
            </Badge>
          </button>
        }
      />
      <TooltipContent
        side="top"
        // `data-testid` propagates through Radix Portal to the rendered
        // tooltip element — consumers pass surface-prefixed tooltip
        // testids (e.g. `creator-detail-alert-rotation-tooltip`).
        data-testid={tooltipTestId}
      >
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  )
}
